// Global packages and components
import { clsx } from 'clsx'
import {
  ContentEditor,
  Button,
  Picture,
  Slider,
  LoaderIcon,
  Video,
} from '~/components'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type {
  SanityButtonProps,
  SanityCustomFile,
  SanityRefWithAlt,
} from '~/types'
import { getLinkType } from '~/utils'

type bannerSizeType = 'skinny' | 'small' | 'medium' | 'large'

type Slide = {
  mediaType: 'image' | 'video' | 'oembed'
  images?: {
    image: SanityRefWithAlt
    imageMobile?: SanityRefWithAlt
  }
  videos?: {
    video?: SanityCustomFile
    videoMobile?: SanityCustomFile
  }
  content?: PortableTextBlock[]
  button?: SanityButtonProps
  oembed?: string
  mobileOembed?: string
  removeOverlay?: boolean
  bannerAsButton?: boolean
  options?: {
    contentAlignment?: 'start' | 'center' | 'end'
    contentVerticalAlignment?: 'start' | 'center' | 'end'
  }
}

interface Props {
  bannerSize: bannerSizeType
  bannerType: 'loop' | 'fade'
  slides: Slide[]
  hideDots?: boolean
}

// Main export
export default function BlockBanner({
  bannerSize,
  bannerType,
  slides,
  hideDots,
}: Props) {
  const bannerSizes = {
    'h-48': bannerSize == 'skinny',
    'h-[28rem]': bannerSize == 'small',
    'h-[35rem]': bannerSize == 'medium',
    'h-[75vh] md:h-[45rem]': bannerSize == 'large',
  }

  return (
    <div className="lg:container lg:mb-16">
      <div className={clsx('relative rounded-2xl bg-neutral-100', bannerSizes)}>
        <Slider
          dotsInside={bannerType === 'fade'}
          className="relative z-[1] h-full"
          hideDots={hideDots}
          options={{
            autoplay: true,
            type: bannerType,
            pagination: false,
            padding: { right: bannerType === 'loop' ? '10%' : 0 },
            breakpoints: {
              1024: {
                padding: bannerType === 'loop' ? '10%' : 0,
                pagination: true,
              },
            },
          }}
        >
          {slides?.map((slide, index) => {
            const bannerLink = getLinkType({
              linkType: slide?.button?.linkType,
              linkReference: slide?.button?.linkReference,
              linkSlug: slide?.button?.linkSlug,
              linkExternal: slide?.button?.linkExternal,
            })

            return (
              <div
                className={clsx('relative mx-2.5', bannerSizes)}
                key={index}
              >
                {slide.bannerAsButton && bannerLink ? (
                  <a href={bannerLink || '#'}>
                    {
                      <SlideContent
                        {...slide}
                        bannerSize={bannerSize}
                      />
                    }
                  </a>
                ) : (
                  <SlideContent
                    {...slide}
                    bannerSize={bannerSize}
                  />
                )}
              </div>
            )
          })}
        </Slider>

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LoaderIcon size="md" />
        </div>
      </div>
    </div>
  )
}

const SlideContent = (slide: Slide & { bannerSize: bannerSizeType }) => {
  const contentAlignment = slide.options?.contentAlignment || 'center'
  const contentVerticalAlignment =
    slide.options?.contentVerticalAlignment || 'center'

  return (
    <>
      <div
        className={clsx(
          'absolute left-0 top-0 z-[2] flex h-full w-full flex-col px-5 text-center text-white md:px-8',
          {
            'py-4': slide.bannerSize === 'skinny',
            'py-10': slide.bannerSize !== 'skinny',
            'items-start': contentAlignment === 'start',
            'items-center': contentAlignment === 'center',
            'items-end': contentAlignment === 'end',
            'justify-start': contentVerticalAlignment === 'start',
            'justify-center': contentVerticalAlignment === 'center',
            'justify-end': contentVerticalAlignment === 'end',
          }
        )}
      >
        <div className="max-w-6xl">
          {slide?.content && <ContentEditor content={slide.content} />}
          {slide?.button && !slide?.bannerAsButton && (
            <div className="pt-6">
              <Button {...slide.button} />
            </div>
          )}
        </div>
        <div className="max-w-3xl" />
      </div>

      {slide?.mediaType === 'video' && slide?.videos?.video ? (
        <div className="absolute left-0 top-0 z-[1] size-full overflow-hidden rounded-2xl">
          <Video
            videoType="file"
            file={slide.videos.video}
            mobileFile={slide.videos?.videoMobile}
            autoplay
            mobileAutoplay
          />
        </div>
      ) : slide?.mediaType === 'oembed' && slide?.oembed ? (
        <div className="absolute left-0 top-0 z-[1] size-full overflow-hidden rounded-2xl">
          <Video
            videoType="oembed"
            oembed={slide.oembed}
            mobileOembed={slide.mobileOembed}
            autoplay
            mobileAutoplay
          />
        </div>
      ) : slide?.mediaType === 'image' && slide?.images?.image ? (
        <Picture
          image={slide.images.image}
          imageMobile={slide.images?.imageMobile}
          classNamePic={clsx(
            'absolute left-0 top-0 z-[1] size-full overflow-hidden rounded-2xl',
            { 'bg-black/30': !slide?.removeOverlay }
          )}
          classNameImg="size-full object-cover mix-blend-multiply"
        />
      ) : null}
    </>
  )
}
